import { Container } from "@/components/layout/Container";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { ArrowRight, Palette, Code, Users, Zap } from "lucide-react";
import { Link } from "wouter";
import { SEO } from "@/components/shared/SEO";

const services = [
  {
    icon: Palette,
    title: "デザイン",
    description: "誰でも使いやすい綺麗なUIデザインを提供します。どんなデザインもご相談ください。",
    points: ["レスポンシブデザイン", "アクセシビリティ対応", "ブランドの反映"]
  },
  {
    icon: Code,
    title: "開発",
    description: "最新技術を活用した高品質な開発を行います。保守性と拡張性を考慮した実装を心がけています。",
    points: ["最新の技術スタック", "素早い対応", "継続的な改善"]
  },
  {
    icon: Users,
    title: "ユーザー中心設計",
    description: "お客様のニーズを汲み取り、使いやすさを追求したデザインを提供します。お客様の声を大切にした改善を行います。",
    points: ["問題点の発見", "フィードバックの反映"]
  },
  {
    icon: Zap,
    title: "パフォーマンス",
    description: "高速で快適なWebアプリケーションを実現します。SEO対策やページ読み込みの最適化を徹底的に行います。",
    points: ["ストレスのない操作感", "SEO最適化"]
  }
];

export default function Home() {
  return (
    <>
      <SEO
        title="Home"
        description="Frontend developer specializing in creating beautiful, functional, and accessible web applications with modern technologies."
        keywords="frontend developer, web development, React, TypeScript, portfolio"
      />
      <section className="min-h-[80vh] flex items-center py-16 md:py-24">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl mx-auto text-center"
          >
            <h1 className="text-4xl md:text-6xl font-bold tracking-tighter mb-8">
              IT for You with Majalis
            </h1>
            <p className="text-lg text-muted-foreground mb-2">
              フロントエンドエンジニア
            </p>
            <p className="text-lg text-muted-foreground mb-2">
              Beautiful × Functional × Accessible    
            </p>
            <p className="text-lg text-muted-foreground mb-8">
              「美しく、使いやすく、そして誰にでも」   
            </p>
            <div className="flex gap-4 justify-center">
              <Link href="/works">
                <Button size="lg">
                  View My Work
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link href="/contact">
                <Button variant="outline" size="lg">
                  Get in Touch
                </Button>
              </Link>
            </div>
          </motion.div>
        </Container>
      </section>

      <section className="py-24 bg-muted">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="max-w-5xl mx-auto"
          >
            <h2 className="text-3xl font-bold tracking-tighter text-center mb-12">サービス内容</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {services.map((service, index) => (
                <motion.div
                  key={service.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <div className="p-6 bg-background rounded-lg hover:shadow-lg transition-shadow">
                    <service.icon className="h-12 w-12 mb-4 text-foreground/80" />
                    <h3 className="text-xl font-semibold mb-3">{service.title}</h3>
                    <p className="text-muted-foreground mb-4">{service.description}</p>
                    <ul className="space-y-2">
                      {service.points.map((point, i) => (
                        <li key={i} className="flex items-center text-sm text-muted-foreground">
                          <span className="w-1.5 h-1.5 rounded-full bg-foreground/60 mr-2" />
                          {point}
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </Container>
      </section>
    </>
  );
}